import React, { useState, useEffect, useCallback } from "react";

import { Input } from "./ui/input";
import { VALID_SECRET_CODES } from "../constants/registration";

interface SecretCodeInputProps {
  onValidCode: (code: string) => void;
  codeLength?: number;
  helperText?: string;
  errorMessage?: string;
  validateCode?: (code: string) => boolean;
}

const SecretCodeInput: React.FC<SecretCodeInputProps> = ({
  onValidCode,
  codeLength = 8,
  helperText,
  errorMessage = "비밀코드가 올바르지 않습니다.",
  validateCode,
}) => {
  const normalizedCodeLength = Math.max(1, codeLength);
  const [secretCode, setSecretCode] = useState(() =>
    Array.from({ length: normalizedCodeLength }, () => ""),
  );
  const [secretCodeError, setSecretCodeError] = useState("");
  const gridClassName =
    normalizedCodeLength === 6
      ? "grid grid-cols-3 gap-3 sm:grid-cols-6"
      : "grid grid-cols-4 gap-3 sm:grid-cols-8";

  const checkSecretCode = useCallback(() => {
    const enteredCode = secretCode.join("");
    const isValid = validateCode
      ? validateCode(enteredCode)
      : VALID_SECRET_CODES.includes(enteredCode);

    if (isValid) {
      onValidCode(enteredCode);
      setSecretCodeError("");
    } else {
      setSecretCodeError(errorMessage);
    }
  }, [errorMessage, onValidCode, secretCode, validateCode]);

  useEffect(() => {
    setSecretCode(Array.from({ length: normalizedCodeLength }, () => ""));
    setSecretCodeError("");
  }, [normalizedCodeLength]);

  useEffect(() => {
    if (secretCode.join("").length === normalizedCodeLength) {
      checkSecretCode();
    } else if (secretCodeError) {
      setSecretCodeError("");
    }
  }, [checkSecretCode, normalizedCodeLength, secretCode, secretCodeError]);

  const focusInput = (index: number) => {
    const nextInput = document.getElementById(
      `secret-code-${index}`,
    ) as HTMLInputElement | null;
    nextInput?.focus();
  };

  const handleSecretCodeChange = (value: string, index: number) => {
    const sanitizedValue = value.replace(/\D/g, "").slice(-1);
    const newCode = [...secretCode];
    newCode[index] = sanitizedValue;
    setSecretCode(newCode);

    if (sanitizedValue && index < secretCode.length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Backspace" && !secretCode[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, normalizedCodeLength);

    if (!pastedValue) return;

    event.preventDefault();

    const nextCode = Array.from(
      { length: normalizedCodeLength },
      (_, index) => pastedValue[index] ?? "",
    );
    setSecretCode(nextCode);

    if (pastedValue.length < normalizedCodeLength) {
      focusInput(pastedValue.length);
    }
  };

  return (
    <div className="space-y-4">
      <div className={gridClassName}>
        {secretCode.map((digit, index) => (
          <Input
            key={index}
            id={`secret-code-${index}`}
            value={digit}
            onChange={(e) => handleSecretCodeChange(e.target.value, index)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            onPaste={handlePaste}
            maxLength={1}
            inputMode="numeric"
            autoComplete="one-time-code"
            className={`h-12 rounded-lg border bg-slate-50 text-center text-lg font-semibold text-slate-900 focus-visible:ring-1 focus-visible:ring-[#fb5120] focus-visible:ring-offset-0 ${
              secretCodeError ? "border-rose-300" : "border-slate-200"
            }`}
          />
        ))}
      </div>

      <p className="text-sm text-slate-500">
        {helperText ??
          `${normalizedCodeLength}자리 비밀코드를 입력하면 바로 확인합니다.`}
      </p>

      {secretCodeError && (
        <p className="text-sm text-rose-600">{secretCodeError}</p>
      )}
    </div>
  );
};

export default SecretCodeInput;
